import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { EntityImageGallery } from '../components/EntityImage';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import {
  Target,
  Edit,
  Trash2,
  ArrowLeft,
  Calendar,
  DollarSign,
  Hash,
  Ruler,
  Crosshair,
  Package,
  Eye,
  EyeOff,
  TrendingUp,
  TrendingDown,
  Minus,
  Tag,
} from 'lucide-react';
import { toast } from 'sonner';

export default function FirearmDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [firearm, setFirearm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [showSerial, setShowSerial] = useState(false);
  // selectedImage state removed - handled by EntityImageGallery

  useEffect(() => {
    const fetchFirearm = async () => {
      try {
        const response = await api.get(`/firearms/${id}`);
        setFirearm(response.data);
      } catch (error) {
        console.error('Error fetching firearm:', error);
        toast.error('Firearm not found');
        navigate('/firearms');
      } finally {
        setLoading(false);
      }
    };

    fetchFirearm();
  }, [id, navigate]);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/firearms/${id}`);
      toast.success('Firearm deleted');
      navigate('/firearms');
    } catch (error) {
      console.error('Error deleting firearm:', error);
      toast.error('Failed to delete firearm');
    } finally {
      setDeleting(false);
    }
  };

  const getTagColor = (tag) => {
    const colors = {
      'EDC': 'bg-secondary/20 text-secondary border-secondary/30',
      'Range': 'bg-accent/20 text-accent border-accent/30',
      'Home Defense': 'bg-destructive/20 text-destructive border-destructive/30',
      'Collector': 'bg-primary/20 text-primary border-primary/30',
    };
    return colors[tag] || 'bg-muted text-muted-foreground border-muted';
  };

  const maskSerial = (serial) => {
    if (!serial) return 'N/A';
    if (serial.length <= 4) return '****';
    return '****' + serial.slice(-4);
  };

  if (loading) {
    return (
      <Layout title="Firearm Details">
        <div className="animate-pulse space-y-6">
          <div className="aspect-[16/9] max-w-2xl bg-muted rounded-sm" />
          <div className="h-8 bg-muted rounded w-1/3" />
          <div className="h-4 bg-muted rounded w-1/2" />
        </div>
      </Layout>
    );
  }

  if (!firearm) {
    return (
      <Layout title="Firearm Details">
        <div className="text-center py-16">
          <Target className="w-16 h-16 text-muted-foreground/50 mx-auto mb-4" />
          <h3 className="text-lg font-semibold">Firearm Not Found</h3>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Firearm Details">
      <div className="space-y-6" data-testid="firearm-detail-page">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate('/firearms')}
          className="mb-4"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Firearms
        </Button>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Image Gallery - Now using EntityImageGallery with object URLs */}
          <EntityImageGallery
            imageFileIds={firearm.images}
            alt={`${firearm.manufacturer} ${firearm.model}`}
            className="aspect-[4/3] bg-card border border-border rounded-sm overflow-hidden bg-muted"
            thumbnailClassName="w-full h-full object-cover"
            fallback={
              <div className="w-full h-full flex items-center justify-center bg-muted">
                <Target className="w-24 h-24 text-muted-foreground/30" />
              </div>
            }
          />

          {/* Details */}
          <div className="space-y-6">
            {/* Header */}
            <div>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h1 className="text-3xl font-bold text-foreground font-['Chivo'] tracking-tight">
                    {firearm.manufacturer}
                  </h1>
                  <h2 className="text-xl text-muted-foreground">{firearm.model}</h2>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/firearms/${id}/edit`)}
                    className="rounded-sm"
                    data-testid="edit-btn"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Edit
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-sm text-destructive hover:bg-destructive hover:text-destructive-foreground"
                        data-testid="delete-btn"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete Firearm</AlertDialogTitle>
                        <AlertDialogDescription>
                          Are you sure you want to delete this firearm? This action cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={handleDelete}
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          disabled={deleting}
                          data-testid="confirm-delete-btn"
                        >
                          {deleting ? 'Deleting...' : 'Delete'}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>

              {/* Tags */}
              {firearm.tags?.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-4">
                  {firearm.tags.map(tag => (
                    <Badge
                      key={tag}
                      variant="outline"
                      className={`rounded-sm ${getTagColor(tag)}`}
                    >
                      {tag}
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* Specifications */}
            <div className="bg-card border border-border rounded-sm p-6 space-y-4">
              <h3 className="text-sm uppercase tracking-widest text-muted-foreground font-['Chivo'] mb-4">
                Specifications
              </h3>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <p className="text-xs uppercase tracking-widest text-muted-foreground">Type</p>
                  <p className="font-medium">{firearm.type}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs uppercase tracking-widest text-muted-foreground">Caliber</p>
                  <p className="font-medium">{firearm.caliber}</p>
                </div>
                {firearm.action && (
                  <div className="space-y-1">
                    <p className="text-xs uppercase tracking-widest text-muted-foreground">Action</p>
                    <p className="font-medium">{firearm.action}</p>
                  </div>
                )}
                {firearm.barrel_length && (
                  <div className="space-y-1">
                    <p className="text-xs uppercase tracking-widest text-muted-foreground">Barrel Length</p>
                    <p className="font-medium">{firearm.barrel_length}</p>
                  </div>
                )}
                {firearm.capacity && (
                  <div className="space-y-1">
                    <p className="text-xs uppercase tracking-widest text-muted-foreground">Capacity</p>
                    <p className="font-medium">{firearm.capacity} rounds</p>
                  </div>
                )}
                {firearm.condition && (
                  <div className="space-y-1">
                    <p className="text-xs uppercase tracking-widest text-muted-foreground">Condition</p>
                    <p className="font-medium">{firearm.condition}</p>
                  </div>
                )}
              </div>

              {/* Serial Number */}
              {firearm.serial_number && (
                <div className="pt-4 border-t border-border">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <p className="text-xs uppercase tracking-widest text-muted-foreground">Serial Number</p>
                      <p className="font-mono font-medium">
                        {showSerial ? firearm.serial_number : maskSerial(firearm.serial_number)}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowSerial(!showSerial)}
                      data-testid="toggle-serial-btn"
                    >
                      {showSerial ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Purchase Info */}
            {(firearm.purchase_date || firearm.purchase_price) && (
              <div className="bg-card border border-border rounded-sm p-6 space-y-4">
                <h3 className="text-sm uppercase tracking-widest text-muted-foreground font-['Chivo'] mb-4">
                  Purchase Information
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  {firearm.purchase_date && (
                    <div className="space-y-1">
                      <p className="text-xs uppercase tracking-widest text-muted-foreground">Purchase Date</p>
                      <p className="font-medium flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-muted-foreground" />
                        {firearm.purchase_date}
                      </p>
                    </div>
                  )}
                  {firearm.purchase_price && (
                    <div className="space-y-1">
                      <p className="text-xs uppercase tracking-widest text-muted-foreground">Purchase Price</p>
                      <p className="font-medium flex items-center gap-2">
                        <DollarSign className="w-4 h-4 text-muted-foreground" />
                        ${firearm.purchase_price.toLocaleString()}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Current Value */}
            <div className="bg-card border border-border rounded-sm p-6 space-y-4">
              <h3 className="text-sm uppercase tracking-widest text-muted-foreground font-['Chivo'] mb-4">
                Current Value
              </h3>
              
              {firearm.current_value ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <p className="text-xs uppercase tracking-widest text-muted-foreground">Estimated Value</p>
                      <p className="text-2xl font-bold font-mono flex items-center gap-2">
                        <DollarSign className="w-5 h-5 text-muted-foreground" />
                        {firearm.current_value.toLocaleString()}
                      </p>
                    </div>
                  </div>

                  {/* Gain/Loss Calculator */}
                  {firearm.purchase_price && (
                    <div className="pt-4 border-t border-border">
                      {(() => {
                        const gainLoss = firearm.current_value - firearm.purchase_price;
                        const percentage = ((gainLoss / firearm.purchase_price) * 100).toFixed(1);
                        const isGain = gainLoss > 0;
                        const isLoss = gainLoss < 0;
                        const isEven = gainLoss === 0;

                        return (
                          <div className={`flex items-center justify-between p-4 rounded-sm ${
                            isGain ? 'bg-green-500/10' : isLoss ? 'bg-red-500/10' : 'bg-muted/50'
                          }`}>
                            <div className="flex items-center gap-3">
                              {isGain ? (
                                <TrendingUp className="w-6 h-6 text-green-500" />
                              ) : isLoss ? (
                                <TrendingDown className="w-6 h-6 text-red-500" />
                              ) : (
                                <Minus className="w-6 h-6 text-muted-foreground" />
                              )}
                              <div>
                                <p className="text-xs uppercase tracking-widest text-muted-foreground">
                                  {isGain ? 'Value Gained' : isLoss ? 'Value Lost' : 'No Change'}
                                </p>
                                <p className={`text-xl font-bold font-mono ${
                                  isGain ? 'text-green-500' : isLoss ? 'text-red-500' : 'text-muted-foreground'
                                }`}>
                                  {isGain ? '+' : ''}{gainLoss < 0 ? '-' : ''}${Math.abs(gainLoss).toLocaleString()}
                                </p>
                              </div>
                            </div>
                            <div className={`text-right px-3 py-1 rounded-sm ${
                              isGain ? 'bg-green-500/20 text-green-500' : isLoss ? 'bg-red-500/20 text-red-500' : 'bg-muted text-muted-foreground'
                            }`}>
                              <span className="text-lg font-bold font-mono">
                                {isGain ? '+' : ''}{percentage}%
                              </span>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-muted-foreground text-sm mb-3">
                    No current value set. Edit this firearm to add an estimated value.
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/firearms/${id}/edit`)}
                    className="rounded-sm"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Add Current Value
                  </Button>
                </div>
              )}
            </div>

            {/* Sale Information */}
            <div className="bg-card border border-border rounded-sm p-6 space-y-4">
              <h3 className="text-sm uppercase tracking-widest text-muted-foreground font-['Chivo'] mb-4 flex items-center gap-2">
                <Tag className="w-4 h-4" />
                Sale Information
              </h3>
              
              {firearm.sale_price ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    {firearm.sale_date && (
                      <div className="space-y-1">
                        <p className="text-xs uppercase tracking-widest text-muted-foreground">Sale Date</p>
                        <p className="font-medium flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-muted-foreground" />
                          {firearm.sale_date}
                        </p>
                      </div>
                    )}
                    <div className="space-y-1">
                      <p className="text-xs uppercase tracking-widest text-muted-foreground">Sale Price</p>
                      <p className="text-2xl font-bold font-mono flex items-center gap-2">
                        <DollarSign className="w-5 h-5 text-muted-foreground" />
                        {firearm.sale_price.toLocaleString()}
                      </p>
                    </div>
                  </div>

                  {/* Sale Gain/Loss Calculator */}
                  {firearm.purchase_price && (
                    <div className="pt-4 border-t border-border">
                      {(() => {
                        const gainLoss = firearm.sale_price - firearm.purchase_price;
                        const percentage = ((gainLoss / firearm.purchase_price) * 100).toFixed(1);
                        const isGain = gainLoss > 0;
                        const isLoss = gainLoss < 0;

                        return (
                          <div className={`flex items-center justify-between p-4 rounded-sm ${
                            isGain ? 'bg-green-500/10' : isLoss ? 'bg-red-500/10' : 'bg-muted/50'
                          }`}>
                            <div className="flex items-center gap-3">
                              {isGain ? (
                                <TrendingUp className="w-6 h-6 text-green-500" />
                              ) : isLoss ? (
                                <TrendingDown className="w-6 h-6 text-red-500" />
                              ) : (
                                <Minus className="w-6 h-6 text-muted-foreground" />
                              )}
                              <div>
                                <p className="text-xs uppercase tracking-widest text-muted-foreground">
                                  {isGain ? 'Profit on Sale' : isLoss ? 'Loss on Sale' : 'Break Even'}
                                </p>
                                <p className={`text-xl font-bold font-mono ${
                                  isGain ? 'text-green-500' : isLoss ? 'text-red-500' : 'text-muted-foreground'
                                }`}>
                                  {isGain ? '+' : ''}{gainLoss < 0 ? '-' : ''}${Math.abs(gainLoss).toLocaleString()}
                                </p>
                              </div>
                            </div>
                            <div className={`text-right px-3 py-1 rounded-sm ${
                              isGain ? 'bg-green-500/20 text-green-500' : isLoss ? 'bg-red-500/20 text-red-500' : 'bg-muted text-muted-foreground'
                            }`}>
                              <span className="text-lg font-bold font-mono">
                                {isGain ? '+' : ''}{percentage}%
                              </span>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  {/* Sold Badge */}
                  <div className="flex justify-center pt-2">
                    <span className="inline-flex items-center gap-2 px-4 py-2 bg-secondary/20 text-secondary rounded-sm text-sm font-medium uppercase tracking-widest">
                      <Tag className="w-4 h-4" />
                      Sold
                    </span>
                  </div>
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-muted-foreground text-sm mb-3">
                    This firearm has not been sold. Record sale details when you sell it.
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/firearms/${id}/edit`)}
                    className="rounded-sm"
                  >
                    <Tag className="w-4 h-4 mr-2" />
                    Record Sale
                  </Button>
                </div>
              )}
            </div>

            {/* Notes */}
            {firearm.notes && (
              <div className="bg-card border border-border rounded-sm p-6">
                <h3 className="text-sm uppercase tracking-widest text-muted-foreground font-['Chivo'] mb-4">
                  Notes
                </h3>
                <p className="text-foreground whitespace-pre-wrap">{firearm.notes}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
